import classNames from "classnames";
import { useEffect, useState } from "react";
import {
	getRawProductWithoutPrices,
	PaymentInterval,
	PaymentIntervalToActiveName,
} from "../../../../../../common/premium_tiers";
import { DashboardUtils } from "../../../dashboard_utils";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";

// Simplified license type for the upgrade modal - only includes fields we need
export interface UpgradeableLicenseInfo {
	id: string;
	product_id: string;
	payment_interval: PaymentInterval;
	activations: number;
}

export interface UpgradeChoiceModalProps {
	open: boolean;
	onClose: () => void;
	licenses: UpgradeableLicenseInfo[];
	onUpgrade: (license: UpgradeableLicenseInfo) => void;
	onBuyNew: () => void;
	isLoading?: boolean;
}

export function UpgradeChoiceModal(props: UpgradeChoiceModalProps) {
	const { open, onClose, licenses, onUpgrade, onBuyNew, isLoading } = props;
	const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(
		licenses.length === 1 ? licenses[0].id : null
	);

	// Auto-select when there's exactly 1 license (handles async loading)
	useEffect(() => {
		if (licenses.length === 1 && !selectedLicenseId) {
			setSelectedLicenseId(licenses[0].id);
		}
	}, [licenses, selectedLicenseId]);

	if (!open) {
		return null;
	}

	const selectedLicense = licenses.find((l) => l.id === selectedLicenseId);

	return (
		<DashboardUtils.SeshModal
			is_active={open}
			onClose={onClose}
			title="Upgrade to sesh Pro"
			subtitle="Choose how you'd like to get sesh Pro"
			content={
				<div className="mt-4 flex flex-col gap-6">
					{/* Option 1: Upgrade existing license */}
					<div className="rounded-lg border border-slate-600 bg-slate-800/50 p-4">
						<div className="mb-3 flex items-center gap-2">
							<i className="fas fa-arrow-up text-sesh-primary"></i>
							<h3 className="text-lg font-semibold text-white">
								Upgrade Existing License
							</h3>
						</div>
						<p className="mb-4 text-sm text-slate-300">
							Upgrade one of your current Premium licenses to sesh
							Pro. You'll keep your current billing cycle and only
							pay the price difference.
						</p>

						{licenses.length > 1 ? (
							<div className="mb-4 flex flex-col gap-2">
								<label className="text-sm font-medium text-slate-200">
									Select a license to upgrade:
								</label>
								<div className="flex flex-col gap-2">
									{licenses.map((license) => {
										const product = getRawProductWithoutPrices(
											license.product_id
										);
										return (
											<label
												key={license.id}
												className={classNames(
													"flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors",
													{
														"border-sesh-primary bg-sesh-primary/10":
															selectedLicenseId ===
															license.id,
														"border-slate-600 hover:border-slate-500":
															selectedLicenseId !==
															license.id,
													}
												)}
											>
												<input
													type="radio"
													name="license"
													value={license.id}
													checked={
														selectedLicenseId ===
														license.id
													}
													onChange={() =>
														setSelectedLicenseId(
															license.id
														)
													}
													className="h-4 w-4 text-sesh-primary focus:ring-sesh-primary"
												/>
												<div className="flex flex-1 items-center justify-between">
													<div className="flex flex-col">
														<span className="font-medium text-white">
															{product?.title ||
																"Premium"}
														</span>
														<span className="text-xs text-slate-400">
															{license.activations}{" "}
															activation
															{license.activations > 1
																? "s"
																: ""}
														</span>
													</div>
													<span className="rounded bg-amber-600/80 px-2 py-1 text-xs font-bold text-white">
														{PaymentIntervalToActiveName[
															license.payment_interval
														]}
													</span>
												</div>
											</label>
										);
									})}
								</div>
							</div>
						) : (
							<div className="mb-4 rounded-md border border-slate-600 bg-slate-900/50 p-3">
								<div className="flex items-center justify-between gap-2">
									<div className="flex items-center gap-2">
										<i className="fas fa-check-circle text-green-400"></i>
										<span className="text-white">
											{getRawProductWithoutPrices(
												licenses[0].product_id
											)?.title || "Premium"}
										</span>
									</div>
									<span className="rounded bg-amber-600/80 px-2 py-1 text-xs font-bold text-white">
										{PaymentIntervalToActiveName[
											licenses[0].payment_interval
										]}
									</span>
								</div>
							</div>
						)}

						<SeshPrimaryButton
							onClick={() => {
								if (selectedLicense) {
									onUpgrade(selectedLicense);
								}
							}}
							disabled={!selectedLicense || isLoading}
							is_loading={isLoading}
							className={classNames("w-full justify-center", {
								"bg-sesh-primary font-semibold !text-base-dark hover:bg-cyan-300":
									selectedLicense && !isLoading,
								"opacity-50": !selectedLicense || isLoading,
							})}
						>
							<i className="fas fa-arrow-up mr-2"></i>
							{isLoading ? "Redirecting to checkout..." : "Upgrade Selected License"}
						</SeshPrimaryButton>
					</div>

					{/* Divider */}
					<div className="flex items-center gap-4">
						<div className="h-px flex-1 bg-slate-600"></div>
						<span className="text-sm text-slate-400">or</span>
						<div className="h-px flex-1 bg-slate-600"></div>
					</div>

					{/* Option 2: Buy new license */}
					<div className="rounded-lg border border-slate-600 bg-slate-800/50 p-4">
						<div className="mb-3 flex items-center gap-2">
							<i className="fas fa-plus text-amber-400"></i>
							<h3 className="text-lg font-semibold text-white">
								Buy New sesh Pro License
							</h3>
						</div>
						<p className="mb-4 text-sm text-slate-300">
							Purchase a separate sesh Pro license. This will be a
							new subscription independent of your existing
							Premium license(s).
						</p>

						<SeshPrimaryButton
							onClick={onBuyNew}
							className="w-full justify-center border-2 border-amber-400 font-semibold !text-amber-400 hover:bg-amber-400/10"
						>
							<i className="fas fa-shopping-cart mr-2"></i>
							Buy New License
						</SeshPrimaryButton>
					</div>
				</div>
			}
		/>
	);
}
